import { ProductBadgesIcons } from "assets/icons";
import { t } from "i18next";
import { useMemo } from "react";

import { ProductBadgeCategory, ProductBadgeLabel } from "../../constants";
import { ProductBadgeProps } from "./ProductBadge.types";

import styles from "./ProductBadge.module.scss";

const JEWELRY_STYLE_MAP: Record<string, string> = {
  [ProductBadgeLabel.StainlessSteel]: "steel",
  [ProductBadgeLabel.GoldPlated]: "gold",
  [ProductBadgeLabel.FashionJewelry]: "fashion",
  [ProductBadgeLabel.Silver]: "silver"
};

const JEWELRY_TEXT_STYLES: Record<
  string,
  Record<
    string,
    {
      color: string;
      fontWeight: number;
      backgroundColor?: string;
      categoryBorder?: string;
    }
  >
> = {
  Jewelry: {
    steel: {
      color: "#3C4453",
      fontWeight: 400,
      backgroundColor: "#EFF3F6",
      categoryBorder: "1px solid rgba(141, 148, 158, 0.7)"
    },
    gold: {
      color: "#906807",
      fontWeight: 400,
      backgroundColor: "#D396001A",
      categoryBorder: "1px solid rgba(247, 199, 70, 0.7)"
    },
    fashion: {
      color: "#70478D",
      fontWeight: 400,
      backgroundColor: "#7700D31A",
      categoryBorder: "1px solid rgba(200, 195, 240, 0.7)"
    },
    silver: {
      color: "#4D5053",
      fontWeight: 400,
      backgroundColor: "#3D3D3D1A",
      categoryBorder: "1px solid rgba(158, 158, 158, 0.7)"
    }
  },
  JewelryStyle: {
    steel: { color: "#3C4453", fontWeight: 500 },
    gold: { color: "#805B02", fontWeight: 400 },
    fashion: { color: "#70478D", fontWeight: 400 },
    silver: { color: "#4D5053", fontWeight: 400 }
  }
};

const ProductBadge = ({ badge }: ProductBadgeProps) => {
  const badgeColors = useMemo(() => {
    switch (badge.category) {
      case ProductBadgeCategory.Deals:
        return {
          backgroundColor: "#f4e5f8",
          color: "#C80000"
        };
      case ProductBadgeCategory.Event:
        return {
          backgroundColor: "#f4e5f8",
          color: "#9802BF"
        };
      case ProductBadgeCategory.Buzz:
        return {
          backgroundColor: "#ede7fc",
          color: "#561BE0"
        };

      case ProductBadgeCategory.Style:
        return {
          backgroundColor: "#f7e6eb",
          color: "#D90049"
        };
      case ProductBadgeCategory.Craft:
        return {
          backgroundColor: "#f3f0ef",
          color: "#8D6E63"
        };

      case ProductBadgeCategory.Tech:
        return {
          backgroundColor: "#e8faff",
          color: "#006F8D"
        };

      case ProductBadgeCategory.Soon:
        return {
          backgroundColor: "#fff5e5",
          color: "#9B6100"
        };

      case ProductBadgeCategory.Health:
        return {
          backgroundColor: "#e7f6ec",
          color: "#1E8E3E"
        };

      default:
        return {
          backgroundColor: "#fb39731a",
          color: "#fb3973"
        };
    }
  }, [badge]);

  const BadgeIcon = useMemo(() => {
    switch (badge.label) {
      case ProductBadgeLabel.LowPrice:
        return ProductBadgesIcons.LowPriceIcon;
      case ProductBadgeLabel.FlashDeal:
        return ProductBadgesIcons.FlashIcon;
      case ProductBadgeLabel.SpecialOffer:
        return ProductBadgesIcons.SpecialOfferIcon;
      case ProductBadgeLabel.LimitedTimeSale:
        return ProductBadgesIcons.TimeLimitedIcon;
      case ProductBadgeLabel.LowStock:
        return ProductBadgesIcons.LowStockIcon;
      case ProductBadgeLabel.ExclusiveDiscount:
        return ProductBadgesIcons.ExclusiveDiscountIcon;
      case ProductBadgeLabel.BestPriceGuaranteed:
        return ProductBadgesIcons.BestPriceIcon;

      case ProductBadgeLabel.PerfectGift:
        return ProductBadgesIcons.PerfectGiftIcon;
      case ProductBadgeLabel.ExclusiveLaunch:
        return ProductBadgesIcons.ExclusiveLaunch;
      case ProductBadgeLabel.RamadanSpecial:
        return ProductBadgesIcons.RamadanSpecial;
      case ProductBadgeLabel.TabaskiSpecial:
        return ProductBadgesIcons.TabaskiSpecial;
      case ProductBadgeLabel.ChristmasEdition:
        return ProductBadgesIcons.ChristmasEdition;

      case ProductBadgeLabel.Trending:
        return ProductBadgesIcons.TrendingIcon;
      case ProductBadgeLabel.ProPick:
        return ProductBadgesIcons.ProPickIcon;

      case ProductBadgeLabel.RomanticVibes:
        return ProductBadgesIcons.RomanticVibesIcon;
      case ProductBadgeLabel.ForKids:
        return ProductBadgesIcons.ForKidsIcon;
      case ProductBadgeLabel.StyleOfTheDay:
        return ProductBadgesIcons.StyleOfTheDayIcon;
      case ProductBadgeLabel.ModestStylish:
        return ProductBadgesIcons.ModestAndStylishIcon;

      case ProductBadgeLabel.Handmade:
        return ProductBadgesIcons.HandmadeIcon;
      case ProductBadgeLabel.LimitedEdition:
        return ProductBadgesIcons.LimitedEditionIcon;

      case ProductBadgeLabel.LongBatteryLife:
        return ProductBadgesIcons.LongBatteryLifeIcon;
      case ProductBadgeLabel.UniversalCompatibility:
        return ProductBadgesIcons.UniversalCompatibilityIcon;
      case ProductBadgeLabel.WaterResistant:
        return ProductBadgesIcons.WaterResistantIcon;

      case ProductBadgeLabel.AssinieVibes:
        return ProductBadgesIcons.AssinineVibesIcon;
      case ProductBadgeLabel.WorkingGirl:
        return ProductBadgesIcons.WorkingGirlIcon;
      case ProductBadgeLabel.ChicClassy:
        return ProductBadgesIcons.ChicAndClassyIcon;

      case ProductBadgeLabel.StainlessSteel:
        return ProductBadgesIcons.StainlessSteelIcon;
      case ProductBadgeLabel.GoldPlated:
        return ProductBadgesIcons.GoldPlatedIcon;
      case ProductBadgeLabel.FashionJewelry:
        return ProductBadgesIcons.FashionJewelryIcon;
      case ProductBadgeLabel.Silver:
        return ProductBadgesIcons.SilverIcon;

      case ProductBadgeLabel.Health:
        return ProductBadgesIcons.HealthIcon;

      default:
        return null;
    }
  }, []);

  const isJewelry = badge.category === ProductBadgeCategory.Jewelry;
  const isJewelryStyle = badge.category === ProductBadgeCategory.JewelryStyle;
  const hasIconBox =
    badge.category === ProductBadgeCategory.Event ||
    badge.category === ProductBadgeCategory.Buzz ||
    badge.category === ProductBadgeCategory.Style ||
    badge.category === ProductBadgeCategory.Tech ||
    badge.category === ProductBadgeCategory.Craft ||
    badge.category === ProductBadgeCategory.Soon ||
    badge.category === ProductBadgeCategory.Health;

  const jewelryVariant = JEWELRY_STYLE_MAP[badge.label] || "steel";

  if (isJewelry) {
    const textStyle = JEWELRY_TEXT_STYLES.Jewelry[jewelryVariant];
    return (
      <div
        className={`${styles.badge} ${styles.badge__jewelry}`}
        style={{
          paddingLeft: 0,
          color: textStyle.color,
          fontWeight: textStyle.fontWeight,
          backgroundColor: textStyle.backgroundColor
        }}
      >
        <div
          className={`${styles.badge__jewelryCategory} ${styles[`badge__jewelryCategory_${jewelryVariant}`]}`}
          style={{ border: textStyle.categoryBorder }}
        >
          {t("Jewelry")}
        </div>

        {BadgeIcon && <BadgeIcon className={styles.badge__jewelryIcon} />}

        {t(badge.label)}
      </div>
    );
  }

  if (isJewelryStyle) {
    const textStyle = JEWELRY_TEXT_STYLES.JewelryStyle[jewelryVariant];
    return (
      <div
        className={`${styles.badge} ${styles.badge__jewelry} ${styles[`badge__jewelry_${jewelryVariant}`]}`}
        style={{ color: textStyle.color, fontWeight: textStyle.fontWeight }}
      >
        {BadgeIcon && <BadgeIcon className={styles.badge__jewelryIcon} />}

        {t(badge.label)}
      </div>
    );
  }

  return (
    <div
      className={styles.badge}
      style={{
        backgroundColor: badgeColors.backgroundColor,
        color: badgeColors.color
      }}
    >
      {!hasIconBox && (
        <div
          className={styles.badge_category}
          style={{ backgroundColor: badgeColors.color }}
        >
          {t(badge.category)}
        </div>
      )}

      {BadgeIcon && hasIconBox ? (
        <div
          className={styles.badge_iconBox}
          style={{ backgroundColor: badgeColors.color }}
        >
          <BadgeIcon className={styles.badge_iconBox_icon} />
        </div>
      ) : (
        BadgeIcon && (
          <BadgeIcon className={`${styles[`badge_icon__${badge.category}`]}`} />
        )
      )}

      {t(badge.label)}
    </div>
  );
};

export default ProductBadge;
